import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    DynamicFormInputComponent,
    NotificationService
} from '@vendure/admin-ui/core';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { safeJSONParse } from '../utils';
import { NavigationItem, NavigationTargetType, PolicyContentData } from '../ui-types';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';

interface NavigationColumn {
    name: string;
    items: NavigationItem[];
}

@Component({
    selector: 'vdr-navigation-column-input',
    template: `
    <vdr-card>
        <vdr-form-field [label]="'Column Name'">
            <input type="text" [formControl]="nameControl" />
        </vdr-form-field>

        <vdr-form-field [label]="'Navigation Items'">
            <vdr-dynamic-form-input
                #itemsInput
                [control]="itemsControl"
                [def]=navigationItemInputDef
            >
            </vdr-dynamic-form-input>
        </vdr-form-field>
    </vdr-card>
    `,
    standalone: true,
    imports: [SharedModule],
})
export class NavigationColumnInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
    @ViewChild('itemsInput') itemsInput: DynamicFormInputComponent;

    readonly: boolean;
    formControl: FormControl;
    nameControl = new FormControl();
    itemsControl = new FormControl();

    navigationColumnData: NavigationColumn;
    policyContentList: PolicyContentData[] = [];
    policyContentList$?: Observable<PolicyContentData[]>;

    navigationItemInputDef = {
        list: true,
        type: 'string',
        ui: {
            component: 'navigation-item-input',
            options: {
                policyContentList: [] as PolicyContentData[],
                policyContentList$: this.policyContentList$,
                navContext: undefined as any,
            }
        }
    };

    private destroy$ = new Subject<void>();
    private policySub?: Subscription;
    private initializing = true;

    private _config: StringCustomFieldConfig;
    get config(): StringCustomFieldConfig {
        return this._config;
    }
    set config(value: StringCustomFieldConfig) {
        this._config = value;

        const options = value?.ui?.options ?? {};

        // if Vendure reassigns config multiple times, avoid stacking subscriptions
        this.policySub?.unsubscribe();

        const stream = options.policyContentList$;

        // pass through navContext to nested navigation-item-input
        this.navigationItemInputDef = {
            ...this.navigationItemInputDef,
            ui: {
                ...this.navigationItemInputDef.ui,
                options: {
                    ...this.navigationItemInputDef.ui.options,
                    navContext: options.navContext,
                },
            },
        };

        if (stream) {
        this.policySub = stream
            .pipe(takeUntil(this.destroy$))
            .subscribe((list:any) => this.applyPolicyList(list ?? []));
        } else {
        this.applyPolicyList(options.policyContentList ?? []);
        }
    }

    constructor(
        private notificationService: NotificationService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.initializing = true;
        const defaultNavigationColumn: NavigationColumn = { name: '', items: [] };
        this.navigationColumnData = safeJSONParse<NavigationColumn>(
            this.formControl.value,
            this.notificationService,
            defaultNavigationColumn
        )!;
        this.initializeFormControls();
    }

    ngAfterViewInit(): void {
        if (this.itemsInput) {
            this.itemsInput.registerOnChange(this.onItemsChange);
            const navigationItems = this.navigationColumnData?.items?.map((value) => JSON.stringify(value)) || [];
            this.itemsInput.writeValue(navigationItems);
        }
        this.nameControl.valueChanges.subscribe(this.onNameChange);
        setTimeout(() => {
            this.initializing = false;
        });
    }

    ngOnDestroy(): void {
        this.policySub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeFormControls() {
        this.nameControl.setValue(this.navigationColumnData?.name || '', { emitEvent: false });
        const itemsJsonArray = (this.navigationColumnData?.items ?? []).map(i => JSON.stringify(i));
        this.itemsControl.setValue(itemsJsonArray, { emitEvent: false });
    }

    private onNameChange = (value: string | null) => {
        if (this.navigationColumnData && this.navigationColumnData.name !== value) {
            this.navigationColumnData.name = value || '';
            this.updateJsonString();
        }
    };

    private onItemsChange = (items: string[]) => {
        const currentJson = (this.navigationColumnData?.items ?? []).map(i => JSON.stringify(i));
        if (JSON.stringify(currentJson) !== JSON.stringify(items ?? [])) {
            const defaultNavigationItem: NavigationItem = {
                label: '',
                targetType: NavigationTargetType.SitePage,
                pageSlug: '',
                pageSection: '',
                builtInPage: '',
                customSlug: '',
                websiteUrl: '',
                targetProductId: null,
                targetProductSlug: '',
                targetCollectionId: null,
                targetCollectionSlug: '',
            };
            const parsedItems = items?.map(value => safeJSONParse<NavigationItem>(
                value,
                this.notificationService,
                defaultNavigationItem
            )).filter(notNullOrUndefined) ?? [];
            this.navigationColumnData.items = parsedItems as any;
            this.updateJsonString();
        }
    };

    private updateJsonString() {
        const jsonString = JSON.stringify(this.navigationColumnData);
        this.formControl.setValue(jsonString, { emitEvent: !this.initializing });
        if (!this.initializing) {
            this.formControl.markAsDirty();
        }
    }

    private applyPolicyList(list: PolicyContentData[]) {
        this.policyContentList = list;
        // Replace object reference so child dynamic form input sees new options
        this.navigationItemInputDef = {
            ...this.navigationItemInputDef,
            ui: {
                ...this.navigationItemInputDef.ui,
                options: {
                    ...this.navigationItemInputDef.ui.options,
                    policyContentList: [...list],
                    policyContentList$: this._config?.ui?.options?.policyContentList$, // keep passing down
                    navContext: this._config?.ui?.options?.navContext,
                },
            },
        };

        this.cdr.markForCheck();
    }
}
